/**
 * Share-link tokens.
 *
 * 32 random bytes. Guessing one is not a threat model worth building a rate
 * limiter for — which matters here, because this repo has no working limiter:
 * `src/lib/rate-limit.ts` documents that UPSTASH_REDIS_REST_* is set in no
 * Vercel environment, so every limiter in it has always returned the dev-bypass
 * result. The security property is entropy plus expiry plus revocation, and
 * that is stated rather than dressed up.
 *
 * `token_hash` is the ONLY thing any lookup touches — that has not changed.
 *
 * ⚠️ What HAS changed (2026-09-07): a desk member's token is ALSO written to
 * Supabase Vault (`approval_reviewers.token_vault_id`) so an operator can
 * re-copy a link instead of being forced to reissue and silently revoke the one
 * already in the client's inbox. So the old claim — "a dump of
 * `approval_reviewers` yields no working links" — now needs the vault key too,
 * rather than being impossible. The trade is documented where it was made, in
 * `20260907120000_desk_is_per_workspace_not_per_packet.sql`, and is acceptable
 * only because what sits behind these links is pre-publication marketing copy.
 */
const TOKEN_BYTES = 32;

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function generateShareToken(): string {
  const bytes = new Uint8Array(TOKEN_BYTES);
  globalThis.crypto.getRandomValues(bytes);
  return toBase64Url(bytes);
}

export async function hashShareToken(token: string): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Rejects anything that cannot be one of our tokens before it reaches the DB. */
export function isWellFormedShareToken(token: string | null | undefined): boolean {
  if (!token) return false;
  return /^[A-Za-z0-9_-]{40,64}$/.test(token);
}

/**
 * The Content Desk. `/review/:token` still resolves — it redirects here and
 * keeps its token, because links are already in inboxes — but nothing should
 * MINT a `/review/` link any more.
 */
export function shareLinkFor(origin: string, token: string): string {
  return `${origin.replace(/\/$/, '')}/desk/${token}`;
}

export const DEFAULT_SHARE_LINK_DAYS = 30;

export function defaultExpiry(from: Date = new Date()): Date {
  const expiry = new Date(from);
  expiry.setDate(expiry.getDate() + DEFAULT_SHARE_LINK_DAYS);
  return expiry;
}
